import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  FileTextOutlined,
  FolderOutlined,
  MoreOutlined,
  PlusOutlined,
  RollbackOutlined,
  SearchOutlined,
  SolutionOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { Job as ModelJob } from '../models/job.model';
import { JobService, jobScopeFilter } from '../services/job.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { UtilService } from '../../../../../core/services/util.service';
import { useAddJobValidation } from '../../../../../hooks/useAddJobValidation';
import { route } from '../../../../../constants/routes';
import '../../company/components/Company.css';
import './JobDirectory.css';

interface Props {
  action: boolean;
}

export interface JobPostTableRef {
  refresh: () => Promise<void>;
}

type JobTab = 'active' | 'archived';
type JobListItem = ModelJob & { recruiter?: string };

const jobTitle = (job: ModelJob) => job.jobName || job.name || 'Untitled job';

const clientName = (job: ModelJob) =>
  typeof job.client === 'string'
    ? job.client
    : job.client?.name || 'Client not provided';

const recruiterName = (job: JobListItem) =>
  job.recruiter || job.owner?.fullname || 'Not assigned';

const jobApplications = (job: ModelJob) => {
  const count = Number(job.numberOfApplications || job.jobApplicationsSize);
  return Number.isFinite(count) ? count : 0;
};

const jobStatus = (job: ModelJob) => {
  if (job.status) return job.status.toUpperCase();
  if (job.closed) return 'CLOSED';
  if (job.published) return 'PUBLISHED';
  if (job.draft) return 'DRAFT';
  return 'ACTIVE';
};

const formatDate = (value: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const statusTone = (status: string) => {
  if (status === 'PUBLISHED' || status === 'ACTIVE') return 'active';
  if (status === 'DRAFT') return 'attention';
  return 'archived';
};

const JobTable = forwardRef<JobPostTableRef, Props>(({ action }, ref) => {
  const { companyId } = useProfile();
  const navigate = useNavigate();
  const { handleAddJob, checking } = useAddJobValidation();
  const [activeData, setActiveData] = useState<ModelJob[]>([]);
  const [archivedData, setArchivedData] = useState<ModelJob[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeTotal, setActiveTotal] = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [sort, setSort] = useState<string>();
  const [activeTab, setActiveTab] = useState<JobTab>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState<ModelJob | null>(null);
  const [loading, setLoading] = useState(false);
  const countsFetchedRef = useRef(false);
  const didInitialLoad = useRef(false);

  const fetchTabData = useCallback(
    async (tabType: JobTab, fetchDataToo = true) => {
      if (!companyId) return;
      const deletedFilter = jobScopeFilter(tabType);
      if (fetchDataToo) setLoading(true);

      try {
        const params = fetchDataToo
          ? {
              page: pagination.current,
              size: pagination.pageSize,
              ...(sort ? { sort } : {}),
              ...(tabType === 'archived' && !sort
                ? { sort: '-createdOn' }
                : {}),
              filter: deletedFilter,
            }
          : {
              page: 1,
              size: 1,
              filter: deletedFilter,
            };
        const result = await JobService.Get(companyId, params);
        const content = result?.content || [];
        const total = result?.totalElements || 0;

        if (fetchDataToo) {
          if (tabType === 'active') {
            setActiveData(content);
            setActiveTotal(total);
          } else {
            setArchivedData(content);
            setArchivedTotal(total);
          }
        } else if (tabType === 'active') {
          setActiveTotal(total);
        } else {
          setArchivedTotal(total);
        }
      } catch (error) {
        console.error(`Failed to fetch ${tabType} jobs:`, error);
        UtilService.Alert(
          'Jobs unavailable',
          'error',
          'We could not load this part of the job portfolio. Please try again.'
        );
      } finally {
        if (fetchDataToo) setLoading(false);
      }
    },
    [companyId, pagination, sort]
  );

  const fetchInitialData = useCallback(async () => {
    if (!companyId || countsFetchedRef.current) return;
    await Promise.all([
      fetchTabData('active', true),
      fetchTabData('archived', false),
    ]);
    countsFetchedRef.current = true;
  }, [companyId, fetchTabData]);

  useEffect(() => {
    if (companyId && !countsFetchedRef.current && !didInitialLoad.current) {
      void fetchInitialData();
      didInitialLoad.current = true;
    }
  }, [companyId, fetchInitialData]);

  useEffect(() => {
    if (didInitialLoad.current && companyId && countsFetchedRef.current) {
      void fetchTabData(activeTab, true);
    }
  }, [activeTab, companyId, fetchTabData, pagination, sort]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      setPagination({ current: 1, pageSize: 10 });
      setSort(undefined);
      setSearchTerm('');
      setSelectedJob(null);
      countsFetchedRef.current = false;
      didInitialLoad.current = false;
      await fetchInitialData();
      didInitialLoad.current = true;
    },
  }));

  const currentData = useMemo(
    () => (activeTab === 'active' ? activeData : archivedData),
    [activeData, activeTab, archivedData]
  );

  const visibleJobs = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return currentData;
    return currentData.filter((job) =>
      [
        jobTitle(job),
        clientName(job),
        recruiterName(job as JobListItem),
        jobStatus(job),
      ].some((value) => value.toLowerCase().includes(normalized))
    );
  }, [currentData, searchTerm]);

  const publishedOnPage = activeData.filter(
    (job) => jobStatus(job) === 'PUBLISHED'
  ).length;
  const applicationsOnPage = activeData.reduce(
    (total, job) => total + jobApplications(job),
    0
  );

  const openJob = (job: ModelJob) =>
    navigate(route(`/job/${job.id}`), {
      state: {
        jobName: jobTitle(job),
        deleted: activeTab === 'archived',
      },
    });

  const openApplications = (job: ModelJob) =>
    navigate(route(`/job/${job.id}/job-applications`), {
      state: { jobName: jobTitle(job) },
    });

  const archiveJob = async (job: ModelJob) => {
    try {
      await JobService.Delete(job.id);
      setSelectedJob(null);
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', true),
      ]);
      UtilService.Alert(
        'Job archived',
        'success',
        `"${jobTitle(job)}" has been moved to Archived.`
      );
    } catch (error) {
      console.error('Error archiving job:', error);
      UtilService.Alert(
        'Archive unavailable',
        'error',
        'The job could not be archived. Please try again.'
      );
    }
  };

  const restoreJob = async (job: ModelJob) => {
    try {
      await JobService.Restore(job.id);
      setSelectedJob(null);
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', false),
      ]);
      UtilService.Alert(
        'Job restored',
        'success',
        `"${jobTitle(job)}" is available in the active portfolio.`
      );
    } catch (error) {
      console.error('Error restoring job:', error);
      UtilService.Alert(
        'Restore unavailable',
        'error',
        'The job could not be moved back to Active.'
      );
    }
  };

  const confirmArchive = (job: ModelJob) => {
    Modal.confirm({
      title: `Archive ${jobTitle(job)}?`,
      content:
        'The role will leave the active portfolio and remain available under Archived.',
      okText: 'Archive job',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => archiveJob(job),
    });
  };

  const onChange = (
    paginationConfig: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ModelJob> | SorterResult<ModelJob>[]
  ) => {
    if (!Array.isArray(sorter) && sorter.order && sorter.field) {
      const direction = sorter.order === 'ascend' ? '' : '-';
      const field =
        sorter.field === 'numberOfApplications'
          ? 'applications'
          : sorter.field === 'postedOn'
            ? 'createdOn'
            : String(sorter.field);
      setSort(`${direction}${field}`);
    } else {
      setSort(undefined);
    }
    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    });
  };

  const columns: ColumnsType<ModelJob> = [
    {
      title: 'Job',
      dataIndex: 'jobName',
      key: 'jobName',
      sorter: true,
      width: 290,
      render: (_, job) => (
        <div className="company-directory__identity-cell">
          <span className="job-directory__job-icon" aria-hidden>
            <AppstoreOutlined />
          </span>
          <span>
            <strong>{jobTitle(job)}</strong>
            <small>{clientName(job)}</small>
          </span>
        </div>
      ),
    },
    {
      title: 'Recruiter',
      key: 'recruiter',
      width: 170,
      render: (_, job) => (
        <div className="job-directory__recruiter">
          <span aria-hidden>
            {recruiterName(job as JobListItem)
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toUpperCase()}
          </span>
          {recruiterName(job as JobListItem)}
        </div>
      ),
    },
    {
      title: 'Applications',
      dataIndex: 'numberOfApplications',
      key: 'numberOfApplications',
      sorter: true,
      width: 125,
      render: (_, job) => jobApplications(job),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      width: 125,
      render: (_, job) => {
        const status = jobStatus(job);
        return (
          <Tag
            className={`company-directory__status company-directory__status--${statusTone(
              status
            )}`}
          >
            {status.charAt(0) + status.slice(1).toLowerCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Posted',
      dataIndex: 'postedOn',
      key: 'postedOn',
      sorter: true,
      width: 140,
      render: (_, job) => formatDate(job.postedOn || job.postedDate),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 90,
      render: (_, job) => {
        const items = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Open job',
          },
          ...(action
            ? [
                {
                  key: 'applications',
                  icon: <SolutionOutlined />,
                  label: 'View applications',
                },
              ]
            : []),
          ...(action && activeTab === 'active'
            ? [
                {
                  key: 'archive',
                  icon: <FolderOutlined />,
                  label: 'Archive job',
                  danger: true,
                },
              ]
            : []),
          ...(action && activeTab === 'archived'
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Move to active',
                },
              ]
            : []),
        ];
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'view') openJob(job);
                if (key === 'applications') openApplications(job);
                if (key === 'archive') confirmArchive(job);
                if (key === 'restore') void restoreJob(job);
              },
            }}
          >
            <Button
              type="text"
              className="company-directory__more"
              aria-label={`Actions for ${jobTitle(job)}`}
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const renderJobs = (label: string) => (
    <section
      className="company-directory__workspace"
      aria-label={`${label} jobs`}
    >
      <div className="company-directory__toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder="Search this page by title, client, or recruiter"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <span className="company-directory__result-count" aria-live="polite">
          {visibleJobs.length} shown
        </span>
      </div>
      <Table<ModelJob>
        className="company-directory__table"
        columns={columns}
        dataSource={visibleJobs}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: activeTab === 'active' ? activeTotal : archivedTotal,
          showSizeChanger: true,
          showTotal: (total) => `${total} jobs`,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
          onShowSizeChange: (current, pageSize) =>
            setPagination({ current, pageSize }),
        }}
        onChange={onChange}
        onRow={(job) => ({ onClick: () => setSelectedJob(job) })}
        rowClassName={(job) =>
          selectedJob?.id === job.id ? 'company-directory__row--previewed' : ''
        }
        scroll={{ x: 900 }}
      />
    </section>
  );

  const tabItems = [
    {
      key: 'active',
      label: (
        <span>
          Active <b>{activeTotal}</b>
        </span>
      ),
      children: renderJobs('Active'),
    },
    {
      key: 'archived',
      label: (
        <span>
          Archived <b>{archivedTotal}</b>
        </span>
      ),
      children: renderJobs('Archived'),
    },
  ];

  return (
    <main className="company-directory job-directory">
      <header className="company-directory__header">
        <div>
          <span>Talent operations</span>
          <h2>Jobs</h2>
          <p>Manage open roles, publishing, and candidate activity.</p>
        </div>
        <button
          className="company-primary-action"
          type="button"
          disabled={checking}
          onClick={handleAddJob}
        >
          <PlusOutlined aria-hidden />
          {checking ? 'Checking…' : 'Create job'}
        </button>
      </header>

      <section className="company-directory__metrics" aria-label="Job totals">
        <article>
          <span className="company-directory__metric-icon">
            <AppstoreOutlined aria-hidden />
          </span>
          <div>
            <span>Active roles</span>
            <strong>{activeTotal}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <CheckCircleOutlined aria-hidden />
          </span>
          <div>
            <span>Published on page</span>
            <strong>{publishedOnPage}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <FileTextOutlined aria-hidden />
          </span>
          <div>
            <span>Applications on page</span>
            <strong>{applicationsOnPage}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon company-directory__metric-icon--muted">
            <FolderOutlined aria-hidden />
          </span>
          <div>
            <span>Archived</span>
            <strong>{archivedTotal}</strong>
          </div>
        </article>
      </section>

      <div
        className={`company-directory__layout${
          selectedJob ? ' company-directory__layout--preview' : ''
        }`}
      >
        <section className="company-directory__panel">
          <Tabs
            activeKey={activeTab}
            items={tabItems}
            onChange={(key) => {
              setActiveTab(key as JobTab);
              setPagination((current) => ({ ...current, current: 1 }));
              setSearchTerm('');
              setSelectedJob(null);
            }}
          />
        </section>

        {selectedJob ? (
          <aside
            className="company-directory__preview"
            aria-label="Job preview"
          >
            <div className="company-directory__preview-header">
              <strong>Job preview</strong>
              <Button
                type="text"
                icon={<CloseOutlined />}
                aria-label="Close job preview"
                onClick={() => setSelectedJob(null)}
              />
            </div>
            <div className="company-directory__preview-body">
              <div className="company-directory__preview-identity job-directory__preview-identity">
                <span className="company-directory__preview-avatar" aria-hidden>
                  <AppstoreOutlined />
                </span>
                <div>
                  <h3>{jobTitle(selectedJob)}</h3>
                  <span>{clientName(selectedJob)}</span>
                  <Tag
                    className={`company-directory__status company-directory__status--${statusTone(
                      jobStatus(selectedJob)
                    )}`}
                  >
                    {jobStatus(selectedJob).charAt(0) +
                      jobStatus(selectedJob).slice(1).toLowerCase()}
                  </Tag>
                </div>
              </div>

              <section className="company-directory__preview-section">
                <h4>Role details</h4>
                <ul className="company-directory__access-list">
                  <li>
                    <UserOutlined aria-hidden />
                    {recruiterName(selectedJob as JobListItem)}
                  </li>
                  <li>
                    <SolutionOutlined aria-hidden />
                    {jobApplications(selectedJob)} applications
                  </li>
                  <li>
                    <CalendarOutlined aria-hidden />
                    {formatDate(selectedJob.postedOn || selectedJob.postedDate)}
                  </li>
                </ul>
              </section>
            </div>
            <div className="job-directory__preview-footer">
              <button
                className="company-primary-action"
                type="button"
                onClick={() => openJob(selectedJob)}
              >
                <EyeOutlined aria-hidden />
                Open job
              </button>
              {action ? (
                <button
                  className="company-secondary-action"
                  type="button"
                  onClick={() => openApplications(selectedJob)}
                >
                  <SolutionOutlined aria-hidden />
                  View applications
                </button>
              ) : null}
            </div>
          </aside>
        ) : null}
      </div>
    </main>
  );
});

export default JobTable;
